import axios from "axios";
import { API_URL } from "../constants";

export const fetch = (companyId, filtersValue, showAll = false, ids) => {
  const url = `${API_URL}/${companyId}/customers/fetch`;
  const body = { filtersValue, companyId, showAll };
  // Only sent for the "show only selected" mode — an empty/omitted array
  // means the old behaviour (page limit, no id restriction).
  if (Array.isArray(ids) && ids.length > 0) {
    body.ids = ids;
  }
  return axios.post(url, body).then((response) => response.data);
};

/**
 * Server-computed totals (count/turnover/purchaseTurnover/debt/overdueDebt)
 * for a set of customer ids, including ones hidden by the current list
 * filter. Used by the customers list selection bar.
 * @param {string} companyId
 * @param {string[]} ids
 */
export const summary = (companyId, ids) => {
  const url = `${API_URL}/${companyId}/customers/summary`;
  return axios.post(url, { ids }).then((response) => response.data);
};

export const post = (companyId, data) => {
  const url = `${API_URL}/${companyId}/customers`;
  return axios.post(url, data).then((response) => response.data);
};

export const fetchById = (companyId, customerId) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}`;
  return axios.get(url).then((response) => response.data);
};

export const postQuestionnaireDryRun = (companyId, customerId, year) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/post-questionnaire`;
  return axios
    .post(url, { year, dryRun: true })
    .then((response) => response.data);
};

export const sendCreateCustomerQuestionnaire = (
  companyId,
  customerId,
  year,
  options = {}
) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/post-questionnaire`;
  const body = { year };
  if (options.confirmCrossCompanyPrefill) {
    body.confirmCrossCompanyPrefill = true;
  }
  if (options.sendFreshQuestionnaireForm) {
    body.sendFreshQuestionnaireForm = true;
  }
  return axios.post(url, body).then((response) => response.data);
};

export const RequestCustomerApproval = (companyId, customerId, data, year) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/approve`;
  return axios.post(url, { ...data, year }).then((response) => response.data);
};

export const fetchContacts = (
  companyId,
  customerId,
  { signed = true, includeDisabled = false } = {}
) => {
  const url = `${API_URL}/companies/${companyId}/customers/${customerId}/contacts?signed=${signed}&includeDisabled=${includeDisabled}`;
  return axios.get(url).then((response) => response.data);
};

export const postFinance = (companyId, customerId, data, year) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/finance/sign-request`;
  return axios.post(url, { ...data, year }).then((response) => response.data);
};

/**
 * Fetch detailed finance data for a specific year (read-only; no side effects).
 * @param {string} companyId
 * @param {string} customerId
 * @param {number} year
 * @param {string|null|undefined} [financeId] - When provided, returns that specific revision.
 */
export const fetchFinanceDetail = (companyId, customerId, year, financeId = null) => {
  let url = `${API_URL}/${companyId}/customers/${customerId}/finance/${year}`;
  if (financeId) url += `?id=${encodeURIComponent(financeId)}`;
  return axios.get(url).then((response) => response.data);
};

/**
 * Create or update finance for a specific year.
 * When financeId is provided (e.g. draft or revision), that record is updated; otherwise backend finds/creates by customer+year.
 * @param {string} companyId
 * @param {string} customerId
 * @param {number} year
 * @param {object} data
 * @param {string|null|undefined} [financeId] - When provided, updates that finance record.
 */
export const createFinanceForYear = (companyId, customerId, year, data, financeId = null) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/finance`;
  const body = { ...data, year };
  if (financeId) body.financeId = financeId;
  return axios.post(url, body).then((response) => response.data);
};

/**
 * Create a draft finance record (status 'draft') for "Create new" flow. Idempotent: returns existing draft if one exists.
 * Returns { financeRecord }. Use financeRecord.id for URL and subsequent fetch.
 */
export const createFinanceDraft = (companyId, customerId, year) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/finance/draft`;
  return axios.post(url, { year }).then((response) => response.data);
};

/**
 * Regenerate finance document (rebuild PDF with current finance data). Pass financeId when multiple revisions exist.
 * @param {string} companyId
 * @param {string} customerId
 * @param {number} year
 * @param {string|null|undefined} [financeId]
 */
export const regenerateFinanceDocument = (companyId, customerId, year, financeId = null) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/finance/${year}/regenerate-document`;
  const body = financeId ? { financeId } : {};
  return axios.post(url, body).then((response) => response.data);
};

export const fetchCustomersList = (companyId, filtersValue, userId) => {
  const url = `${API_URL}/${companyId}/customers/fetch/${userId}`;
  return axios
    .post(url, { filtersValue, companyId })
    .then((response) => response.data);
};
export const regenerateQuestionnairePdf = (companyId, customerId, customerFormId) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/regenerate-questionnaire-pdf`;
  return axios.post(url, { customerFormId }).then((response) => response.data);
};

export const cancelCustomerQuestionnaire = (companyId, customerId) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/cancel-questionnaire`;
  return axios.post(url).then((response) => response.data);
};

/**
 * Log a Confirm click that failed frontend validation (does not finalize the form).
 * @param {string} formKey
 * @param {{ payload: object, validationErrors: object }} data
 */
export const postQuestionnaireValidationAttempt = (formKey, data) => {
  const url = `${API_URL}/customers/form-key/${formKey}/validation-attempt`;
  return axios.post(url, data).then((response) => response.data);
};

/**
 * Master-only: customers with failed questionnaire validation attempts.
 * @param {string} companyId
 * @param {{ limit?: number, skip?: number }} [query]
 */
export const fetchValidationAttemptCustomers = (companyId, query = {}) => {
  const params = new URLSearchParams();
  if (query.limit != null) params.set("limit", String(query.limit));
  if (query.skip != null) params.set("skip", String(query.skip));
  const qs = params.toString();
  const url = `${API_URL}/${companyId}/customer-form-validation-attempts/customers${
    qs ? `?${qs}` : ""
  }`;
  return axios.get(url).then((response) => response.data);
};

/**
 * Master-only: failed validation attempts for one customer.
 * @param {string} companyId
 * @param {string} customerId
 * @param {{ limit?: number, skip?: number }} [query]
 */
export const fetchValidationAttemptsByCustomer = (
  companyId,
  customerId,
  query = {}
) => {
  const params = new URLSearchParams();
  if (query.limit != null) params.set("limit", String(query.limit));
  if (query.skip != null) params.set("skip", String(query.skip));
  const qs = params.toString();
  const url = `${API_URL}/${companyId}/customers/${customerId}/customer-form-validation-attempts${
    qs ? `?${qs}` : ""
  }`;
  return axios.get(url).then((response) => response.data);
};

export const sendEmailWithAttachment = (companyId, customerId, formData) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/files/post-email`;
  return axios.post(url, formData).then((response) => response.data);
};

export const fetchCustomersListWithHideCheck = (
  companyId,
  filtersValue,
  userId
) => {
  const url = `${API_URL}/${companyId}/customers/hide/fetch/${userId}`;
  return axios
    .post(url, { filtersValue, companyId })
    .then((response) => response.data);
};

// Public-register lookup used when creating a counterparty: one box that takes
// a company name, a company code or a VAT code (erp-backend
// api/services/company-registry).
export const registrySearch = (companyId, q, config = {}) => {
  const url = `${API_URL}/${companyId}/customers/registry/search`;
  return axios.get(url, { params: { q }, ...config }).then((r) => r.data);
};

// The registered address for the row the manager actually picked — a second
// call on purpose, so typing stays fast. A Lithuanian company is found by its
// company code (it may have no VAT code); a foreign one by its VAT code.
export const registryDetails = (
  companyId,
  { companyCode, vatCodeLetters, vatCodeFigures }
) => {
  const url = `${API_URL}/${companyId}/customers/registry/details`;
  return axios
    .get(url, { params: { companyCode, vatCodeLetters, vatCodeFigures } })
    .then((r) => r.data);
};

// Is this counterparty already on file? A VAT code identifies one; without a
// VAT code, the company code does (erp-backend api/services/customers/find-duplicate).
export const checkDuplicate = (companyId, vatCode, companyCode, excludeId) => {
  const url = `${API_URL}/${companyId}/customers/check-duplicate`;
  return axios
    .get(url, { params: { vatCode, companyCode, excludeId } })
    .then((r) => r.data);
};
